import { useEffect } from "react";

const useTrackerJS = () => {
  const expiresInDays = 365;
  const utmParamKeys = [
    "utm_source",
    "utm_campaign",
    "utm_content",
    "utm_medium",
    "utm_group",
    "utm_term",
    "gclid",
    "bd_vid",
    "fbclid",
    "msclkid",
    "keyword",
    "wbraid",
    "gbraid",
    "referral_code",
  ];

  const getParam = (p: any) => {
    const match = RegExp(`[?&]${p}=([^&]*)`).exec(window.location.search);
    return match && decodeURIComponent(match[1].replace(/\+/g, " "));
  };

  const setCookie = ({ cname, cvalue, exdays }: any) => {
    const d = new Date();
    if (!exdays) exdays = expiresInDays;
    d.setTime(d.getTime() + exdays * 1000 * 60 * 60 * 24);
    // @ts-ignore
    const expires = `expires=${d.toGMTString()}`;
    // eslint-disable-next-line no-useless-concat
    window.document.cookie = `${cname}=${cvalue}; ${expires}; ` + `path=/;`;
  };

  const setCookieOnce = ({ cname, cvalue }: any) => {
    const cookie = getCookie(cname);
    if (!cookie) setCookie({ cname, cvalue, exdays: expiresInDays });
  };

  const getCookie = (cname: any) => {
    try {
      const name = `${cname}=`;
      const cArr = window.document.cookie.split(";");
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < cArr.length; i++) {
        const c = cArr[i].trim();
        if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
      }
      return "";
    } catch (e) {
      return "";
    }
  };

  // const cookieNames = [
  //   'initial_utm_params',
  //   'referrer',
  //   'initial_referrer',
  //   'initial_referring_domain',
  //   'referring_domain',
  // ]
  // // CookieStore func to get all cookies
  // const getAllCookies() {
  //   const cookies = cookieStore.getAll()
  //   return cookies
  // }
  // const cleanUpOldCookies() {
  //   const currentCookies = getAllCookies()
  //   currentCookies.map(cookie => {
  //     if (cookieNames.includes(cookie.name) && cookie.path !== '/') {
  //       cookieStore.delete(cookie)
  //     }
  //   })
  // }

  // =========================================================== Referrer
  const isInvalidReferrer = (referrer: any) => {
    if (referrer == "" || referrer == undefined) return true;
    return false;
  };

  const isReferrerInternal = (referrer: any) => {
    const hostname = "amberstudent";
    if (referrer.indexOf(hostname) > -1) return true;
    return false;
  };

  const getCleanUrl = () => {
    let cleanSearch = window.location.search;
    // Remove utm params
    utmParamKeys.forEach((element) => {
      cleanSearch = cleanSearch.replace(RegExp(`${element}[^&]+&?`, "g"), "");
    });
    // remove trailing &
    cleanSearch = cleanSearch.replace(/&$/, "");
    // remove trailing ?
    cleanSearch = cleanSearch.replace(/^\?$/, "");
    return window.location.origin + window.location.pathname + cleanSearch;
  };

  const writeReferrer = () => {
    const { referrer } = document;
    if (!isReferrerInternal(referrer) && !isInvalidReferrer(referrer))
      setCookieOnce({ cname: "referrer", cvalue: referrer });
  };

  const writeInitialReferrer = () => {
    let value = document.referrer;
    if (isInvalidReferrer(value)) value = "direct";
    setCookieOnce({ cname: "initial_referrer", cvalue: value });
  };

  const writeInitialReferringDomain = () => {
    const value = getCleanUrl();
    if (value) setCookieOnce({ cname: "initial_referring_domain", cvalue: value });
  };

  const writeReferringDomain = () => {
    const value = getCleanUrl();
    setCookieOnce({ cname: "referring_domain", cvalue: value });
  };

  // =========================================================== UTM Params
  const writeInitialUtmParams = () => {
    const value = getStringValue();
    if (value.length > 0)
      setCookie({ cname: "initial_utm_params", cvalue: value, exdays: expiresInDays });
  };

  // Write current session UTM params to sessionStorage so they don't
  // bleed across sessions from the 365-day persisted cookie.
  // Always writes — even empty — to clear stale values from earlier in the session.
  const writeSessionUtmParams = () => {
    const currentParams: Record<string, string> = {};
    utmParamKeys.forEach((key) => {
      const val = (getParam(key) || "").trim();
      if (val) currentParams[key] = val;
    });
    try {
      sessionStorage.setItem("session_utm_params", JSON.stringify(currentParams));
    } catch (e) {
      // sessionStorage unavailable (private mode, storage full, etc.)
    }
  };

  // get string value from utm params object.
  const getStringValue = () => {
    const obj = getFinalUtmParams();
    const keys = Object.keys(obj);
    const arr = [];
    if (keys.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < keys.length; ++i) {
        // @ts-ignore
        arr.push(`${keys[i]}=${obj[keys[i]] || ""}`);
      }
    }
    let value = "";
    if (arr.length > 0) value = arr.join("|");
    return value;
  };

  const getFinalUtmParams = () => {
    const finalObj = {};
    const currentObj = {};
    const initialUtmParamsArr = (getCookie("initial_utm_params") || "").split("|");
    initialUtmParamsArr.forEach((element) => {
      const item = element.split("=");
      const key = (item[0] || "").trim();
      const value = (item[1] || "").trim();
      // @ts-ignore
      if (key && value) currentObj[key] = value;
    });
    utmParamKeys.forEach((element) => {
      let urlParamElement = (getParam(element) || "").trim();
      // special case for gclid
      if (element == "gclid") {
        const gclsrc = getParam("gclsrc");
        if (gclsrc && gclsrc.indexOf("aw") == -1) {
          urlParamElement = "";
        }
      }
      // @ts-ignore
      finalObj[element] = urlParamElement || currentObj[element] || "";
    });
    return finalObj;
  };

  const writeInitialAeParams = () => {
    const aeKeys = ["ae_campaign_id", "ae_user_id", "ae_channel"];
    const arr: string[] = [];
    aeKeys.forEach((key) => {
      const value = (getParam(key) || "").trim();
      if (value) arr.push(`${key}=${value}`);
    });
    if (arr.length > 0) {
      setCookie({ cname: "initial_ae_params", cvalue: arr.join("|"), exdays: expiresInDays });
    }
  };

  const Init = () => {
    writeInitialReferrer();
    writeInitialReferringDomain();
    writeReferringDomain();
    writeReferrer();
    writeInitialUtmParams();
    writeSessionUtmParams();
    writeInitialAeParams();
  };

  useEffect(() => {
    Init();
    window.getCookie = getCookie;
    // @ts-ignore
    window.setCookie = setCookie;
  }, []);
};

export default useTrackerJS;
